import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { PkIcon } from '../icon/pk-icon';
import type { PkIconName } from '../icon/pk-icons';

export interface PkDialogData {
  kind: 'confirm' | 'alert' | 'prompt';
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  placeholder?: string;
  initialValue?: string;
  required?: boolean;
  maxLength?: number;
}

/**
 * PkDialogService 내부 다이얼로그 본체 — 직접 open하지 말고 서비스를 통해 쓴다.
 * confirm/alert는 boolean, prompt는 string으로 close한다 (ESC·백드롭은 undefined).
 */
@Component({
  selector: 'pk-dialog-host',
  standalone: true,
  imports: [PkIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="pk-dialog" [attr.data-kind]="data.kind">
      @if (data.title; as title) {
        <h2 class="pk-dialog__title">
          <pk-icon
            class="pk-dialog__icon"
            [class.is-danger]="data.danger === true"
            [name]="titleIcon"
            [size]="18"
          />
          {{ title }}
        </h2>
      }
      <p class="pk-dialog__message">{{ data.message }}</p>
      @if (data.kind === 'prompt') {
        <!-- CDK Dialog 기본 autoFocus(first-tabbable)가 이 input을 잡는다 (버튼보다 앞) -->
        <input
          class="pk-dialog__input"
          type="text"
          [value]="inputValue()"
          [attr.placeholder]="data.placeholder ?? null"
          [attr.maxlength]="data.maxLength ?? null"
          (input)="onInput($event)"
          (keydown.enter)="submit()"
        />
      }
      <div class="pk-dialog__actions">
        @if (data.kind !== 'alert') {
          <button type="button" class="btn pk-dialog__btn" (click)="cancel()">
            {{ data.cancelText ?? '취소' }}
          </button>
        }
        <button
          type="button"
          class="btn pk-dialog__btn pk-dialog__btn--confirm"
          [class.pk-dialog__btn--danger]="data.danger === true"
          [disabled]="confirmDisabled()"
          (click)="submit()"
        >
          {{ data.confirmText ?? '확인' }}
        </button>
      </div>
    </div>
  `,
  styles: `
    .pk-dialog {
      width: 380px;
      max-width: calc(100vw - 48px);
      padding: var(--pk-space-5) var(--pk-space-6);
      background: #fff;
      border-radius: var(--pk-radius);
      box-shadow: var(--pk-shadow-lg);
    }

    .pk-dialog__title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 0 var(--pk-space-2);
      font-size: 16px;
      font-weight: 700;
      color: var(--pk-text);
    }

    /* 유형별 제목 아이콘 — confirm 물음표(브랜드) · danger 경고(빨강) · alert 안내(파랑) */
    .pk-dialog__icon {
      margin-right: 0; /* 전역 h2 > pk-icon 여백은 flex gap이 대신한다 */
      color: var(--pk-green-dark);

      &.is-danger {
        color: var(--pk-danger);
      }
    }

    .pk-dialog[data-kind='alert'] .pk-dialog__icon {
      color: var(--pk-info);
    }

    .pk-dialog__message {
      margin: 0;
      font-size: 14px;
      line-height: 1.6;
      color: var(--pk-text-sub);
      white-space: pre-line; /* \n으로 여러 줄 안내 허용 */
    }

    .pk-dialog__input {
      width: 100%;
      box-sizing: border-box;
      margin-top: var(--pk-space-3);
      padding: 8px 10px;
      border: 1px solid var(--pk-border);
      border-radius: var(--pk-radius-sm);
      font-size: 14px;

      &:focus {
        outline: 2px solid var(--pk-green);
        outline-offset: -1px;
      }
    }

    .pk-dialog__actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--pk-space-2);
      margin-top: var(--pk-space-5);
    }

    .pk-dialog__btn {
      padding: 8px 16px;
      border: 1px solid var(--pk-border);
      border-radius: var(--pk-radius-sm);
      background: #fff;
      color: var(--pk-text);
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: var(--pk-bg);
      }

      &:disabled {
        color: var(--pk-text-muted);
        cursor: default;
      }
    }

    .pk-dialog__btn--confirm {
      border-color: var(--pk-green-dark);
      background: var(--pk-green);
      color: #fff;

      &:hover:not(:disabled) {
        background: var(--pk-green-dark);
      }

      &:disabled {
        border-color: var(--pk-border);
        background: #eef1ef;
      }
    }

    .pk-dialog__btn--danger {
      border-color: #96291f;
      background: var(--pk-danger);

      &:hover:not(:disabled) {
        background: #96291f;
      }
    }
  `,
})
export class PkDialogHost {
  private readonly ref = inject<DialogRef<boolean | string>>(DialogRef);
  protected readonly data = inject<PkDialogData>(DIALOG_DATA);

  protected readonly inputValue = signal(this.data.initialValue ?? '');

  /** kind/danger로 정해지는 제목 아이콘 — 호출부가 고르지 않는다 (의미 일관성) */
  protected readonly titleIcon: PkIconName =
    this.data.danger === true
      ? 'octagon-alert'
      : this.data.kind === 'alert'
        ? 'info'
        : this.data.kind === 'prompt'
          ? 'pencil-line'
          : 'circle-question';

  protected confirmDisabled(): boolean {
    return (
      this.data.kind === 'prompt' &&
      this.data.required === true &&
      this.inputValue().trim() === ''
    );
  }

  protected onInput(event: Event): void {
    this.inputValue.set((event.target as HTMLInputElement).value);
  }

  protected submit(): void {
    if (this.confirmDisabled()) {
      return;
    }
    this.ref.close(this.data.kind === 'prompt' ? this.inputValue() : true);
  }

  protected cancel(): void {
    this.ref.close(undefined);
  }
}
